// ------------------------------------------------------------
// Application Routes Configuration
// ------------------------------------------------------------
// This file defines all routes for the application using
// react-router-dom v6, including public/auth routes and
// protected/dashboard routes.
//
// Features included:
// - AuthGuard: Redirects authenticated users away from auth pages.
// - ProtectedRoute: Restricts access to authenticated users only.
// - Layouts: AuthLayout and DashboardLayout for different sections.
// - Pages: Login, ForgotPassword, ResetPassword, Dashboard, etc.
// ------------------------------------------------------------

import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Layouts & Guards
import AuthLayout from "@/layouts/AuthLayout";
import DashboardLayout from "@/layouts/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Pages
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/dashboard/Dashboard";
import Settings from "@/pages/settings/Settings";
import Analytics from "./pages/analytics/Analytics";
import TenantOrganisations from "./pages/tenantOrg/TenantOrg";
import CreateOrganisation from "./pages/tenantOrg/CreateOrganisation";
import ExperienceEditor from "./pages/experiences/ExperienceEditor";
import TenantProfile from "./pages/tenantOrg/TenantProfile";
import SimulationTemplates from "./pages/Simulation/SimulationTemplates";
import ExperienceListing from "./pages/experiences/ExperienceListing";
import TenantAnalytics from "./pages/analytics/TenantAnalytics";
import OnboardingEditor from "./pages/tenantOrg/OnboardingEditor";

/**
 * AuthGuard
 * ------------------------------------------------------------
 * Prevents authenticated users from accessing authentication-only
 * routes (e.g., Login, ForgotPassword, ResetPassword).
 * Redirects them to the dashboard if already logged in.
 */
const AuthGuard = ({ children }) => {
  const { userToken } = useSelector((state) => state.global);
  return userToken ? <Navigate to="/dashboard" replace /> : children;
};

/**
 * routes
 * ------------------------------------------------------------
 * Application route configuration:
 * 1. Public/Auth Routes (wrapped in AuthLayout + AuthGuard).
 * 2. Protected/Dashboard Routes (wrapped in DashboardLayout + ProtectedRoute).
 * 3. Fallback Route: Redirects unknown paths to login.
 */
export const routes = [
  // ----------------------------
  // Public / Authentication Routes
  // ----------------------------
  {
    path: "/",
    element: <AuthLayout />,
    children: [
      { path: "", element: <Navigate to="/login" replace /> },
      {
        path: "login",
        element: (
          <AuthGuard>
            <Login />
          </AuthGuard>
        ),
      },

      {
        path: "reset-password",
        element: (
          <AuthGuard>
            <ResetPassword />
          </AuthGuard>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <AuthGuard>
            <ForgotPassword />
          </AuthGuard>
        ),
      },
    ],
  },

  // ----------------------------
  // Protected / Dashboard Routes
  // ----------------------------
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "dashboard", element: <Dashboard /> },
      { path: "tenants", element: <TenantOrganisations /> },
      { path: "tenants/create-organisation", element: <CreateOrganisation /> },
      { path: "analytics", element: <Analytics /> },
      { path: "analytics/tenant-details", element: <TenantAnalytics /> },
      { path: "simulation-templates", element: <SimulationTemplates /> },
      {
        path: "tenants/campaigns/experiences/edit",
        element: <ExperienceEditor />,
      },
      { path: "tenants/campaigns/experiences", element: <ExperienceListing /> },
      { path: "tenants/profile", element: <TenantProfile /> },
      { path: "settings", element: <Settings /> },
      { path: "tenants/application-onboarding/edit", element: <OnboardingEditor /> },
      
    ],
  },

  // ----------------------------
  // Fallback Route
  // ----------------------------
  {
    path: "*",
    element: <Navigate to="/login" replace />,
  },
];
